// Trust & Compliance Command Center
// Admin surface, every readiness score is derived from actual runtime state
// (see backend/routes/trust_compliance.py). Framework crosswalk visible per
// control: SOC 2 · NIST CSF 2.0 · CIS v8.1 · OWASP ASVS 5.0 · FERPA · COPPA
// · NC 115C-401.2 · NC 2026 AI Rule.

import { useEffect, useState } from 'react';
import axios from 'axios';
import { AppLayout } from '../components/Layout';
import {
  Shield, Lock, Eye, AlertTriangle, CheckCircle, XCircle,
  FileText, Server, Cpu, AlertCircle, Users, Database, ChevronRight,
  Activity, Clock, ShieldAlert, Book, Zap,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem('futurepath_token') || localStorage.getItem('futurepath_token')}` } });

const STATUS_STYLES = {
  VERIFIED: { color: '#34d399', bg: 'rgba(52,211,153,0.12)', border: 'rgba(52,211,153,0.35)', label: 'Verified' },
  IMPLEMENTED: { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)', border: 'rgba(96,165,250,0.35)', label: 'Implemented' },
  PARTIAL: { color: '#fbbf24', bg: 'rgba(251,191,36,0.12)', border: 'rgba(251,191,36,0.35)', label: 'Partial' },
  NOT_IMPLEMENTED: { color: '#f87171', bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.35)', label: 'Not implemented' },
  FAILED: { color: '#f87171', bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.35)', label: 'Failed' },
  EXCEPTION: { color: '#c084fc', bg: 'rgba(192,132,252,0.12)', border: 'rgba(192,132,252,0.35)', label: 'Exception' },
};

const PILOT_STYLES = {
  PILOT_READY: { color: '#34d399', bg: 'rgba(52,211,153,0.15)', border: 'rgba(52,211,153,0.4)', label: 'PILOT READY' },
  READY_WITH_CONDITIONS: { color: '#fbbf24', bg: 'rgba(251,191,36,0.15)', border: 'rgba(251,191,36,0.4)', label: 'READY WITH CONDITIONS' },
  NOT_READY: { color: '#f87171', bg: 'rgba(248,113,113,0.15)', border: 'rgba(248,113,113,0.4)', label: 'NOT READY' },
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: Activity },
  { id: 'pilot-gate', label: 'Pilot Readiness', icon: ShieldAlert },
  { id: 'controls', label: 'Security Controls', icon: Shield },
  { id: 'audit', label: 'Audit Logs', icon: FileText },
  { id: 'incidents', label: 'Incidents', icon: AlertTriangle },
  { id: 'vulnerabilities', label: 'Vulnerabilities', icon: AlertCircle },
  { id: 'ai-governance', label: 'AI Governance', icon: Cpu },
  { id: 'vendors', label: 'Vendors & Subprocessors', icon: Server },
];

function ReadinessCard({ label, value, icon: Icon, tint }) {
  const pct = value ?? 0;
  const color = pct >= 85 ? '#34d399' : pct >= 60 ? '#fbbf24' : '#f87171';
  return (
    <div className="p-5 rounded-2xl border border-white/10"
      style={{ background: `linear-gradient(140deg, ${tint} 0%, rgba(11,31,56,0.9) 100%)` }}
      data-testid={`readiness-card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color }} />
        <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-slate-300">{label}</p>
      </div>
      <div className="flex items-end gap-2">
        <span className="font-outfit text-4xl font-bold text-white tabular-nums">{pct}%</span>
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
        <div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.NOT_IMPLEMENTED;
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold"
      style={{ color: s.color, background: s.bg, border: `1px solid ${s.border}` }}
      data-testid={`status-pill-${status}`}>
      {status === 'VERIFIED' ? <CheckCircle className="h-3 w-3" /> :
       status === 'FAILED' || status === 'NOT_IMPLEMENTED' ? <XCircle className="h-3 w-3" /> :
       <Clock className="h-3 w-3" />}
      {s.label}
    </span>
  );
}

function FrameworkTags({ c }) {
  const tags = [
    c.soc2 && `SOC 2 ${c.soc2}`, c.nist && `NIST ${c.nist}`,
    c.cis && `CIS ${c.cis}`, c.asvs && `ASVS ${c.asvs}`,
    c.ferpa && `FERPA ${c.ferpa}`, c.coppa && `COPPA ${c.coppa}`,
    c.nc && `NC ${c.nc}`,
  ].filter(Boolean);
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map(t => (
        <span key={t} className="rounded-md border border-white/10 bg-white/[0.03] px-1.5 py-0.5 text-[9.5px] text-slate-300">
          {t}
        </span>
      ))}
    </div>
  );
}

export default function TrustCompliancePage() {
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [controls, setControls] = useState([]);
  const [audit, setAudit] = useState(null);
  const [incidents, setIncidents] = useState(null);
  const [vulns, setVulns] = useState(null);
  const [aiGov, setAiGov] = useState(null);
  const [vendors, setVendors] = useState(null);
  const [pilotGate, setPilotGate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [o, c, a, inc, v, ai, vd, pg] = await Promise.all([
          axios.get(`${API}/admin/trust/overview`, auth()),
          axios.get(`${API}/admin/trust/controls`, auth()),
          axios.get(`${API}/admin/trust/audit-logs?limit=25`, auth()),
          axios.get(`${API}/admin/trust/incidents`, auth()),
          axios.get(`${API}/admin/trust/vulnerabilities`, auth()),
          axios.get(`${API}/admin/trust/ai-governance`, auth()),
          axios.get(`${API}/admin/trust/subprocessors`, auth()),
          axios.get(`${API}/admin/trust/pilot-gate`, auth()),
        ]);
        setOverview(o.data);
        setControls(c.data.controls || []);
        setAudit(a.data);
        setIncidents(inc.data);
        setVulns(v.data);
        setAiGov(ai.data);
        setVendors(vd.data);
        setPilotGate(pg.data);
      } catch (e) {
        setErr(e.response?.data?.detail || 'Access denied. Security/compliance admin only.');
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <AppLayout><div className="p-10 text-center text-slate-300">Loading Trust &amp; Compliance…</div></AppLayout>;
  if (err) return <AppLayout><div className="p-10 text-center text-rose-300">{err}</div></AppLayout>;

  return (
    <AppLayout>
      <div className="min-h-screen text-white pb-20" style={{ background: 'linear-gradient(180deg, #050b18 0%, #0b1f38 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 text-xs text-cyan-300/80 mb-2">
              <Shield className="h-3.5 w-3.5" /> ADMIN → TRUST &amp; COMPLIANCE
            </div>
            <h1 className="font-outfit text-3xl sm:text-4xl font-bold text-white">Trust &amp; Compliance Command Center</h1>
            <p className="mt-2 text-sm text-slate-300 max-w-3xl">
              Real-time security &amp; compliance state, computed from live runtime controls. Every score derived
              from actual verification, not a manual toggle. Mapped to <b>SOC 2 TSC</b>, <b>NIST CSF 2.0</b>,
              <b> CIS v8.1</b>, <b>OWASP ASVS 5.0</b>, <b>FERPA</b>, <b>COPPA</b>, <b>NC 115C-401.2</b>, and the <b>NC 2026 AI Rule</b>.
            </p>
            <p className="mt-2 text-xs text-amber-300/80">
              <b>Positioning:</b> SOC 2 Aligned / SOC 2 Readiness. Not represented as SOC 2 attested until an
              independent CPA examination is complete.
            </p>
          </div>

          {/* Pilot readiness banner */}
          {overview && (() => {
            const p = PILOT_STYLES[overview.pilot_readiness] || PILOT_STYLES.NOT_READY;
            return (
              <div className="mb-6 p-4 rounded-2xl border flex flex-wrap items-center gap-4"
                style={{ background: p.bg, borderColor: p.border }}
                data-testid="pilot-readiness-banner">
                <div className="flex items-center gap-3">
                  <ShieldAlert className="h-6 w-6" style={{ color: p.color }} />
                  <div>
                    <p className="text-[10.5px] uppercase tracking-[0.16em] text-slate-300">Pilot Readiness</p>
                    <p className="font-outfit text-xl font-bold" style={{ color: p.color }}>{p.label}</p>
                  </div>
                </div>
                <div className="ml-auto flex gap-6 text-xs">
                  <div><span className="text-slate-300">Critical verified</span> <b className="text-white ml-1">{overview.critical_verified}/{overview.critical_total}</b></div>
                  <div><span className="text-slate-300">Critical failing</span> <b className="text-rose-300 ml-1">{overview.critical_failed}</b></div>
                  <div><span className="text-slate-300">Open incidents</span> <b className="text-white ml-1">{overview.open_incidents}</b></div>
                  <div><span className="text-slate-300">Critical vulns</span> <b className="text-white ml-1">{overview.critical_vulnerabilities}</b></div>
                </div>
              </div>
            );
          })()}

          {/* Tabs */}
          <div className="flex gap-1.5 mb-6 overflow-x-auto pb-2" data-testid="trust-tabs">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                data-testid={`trust-tab-${t.id}`}
                className={`flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-[12px] font-semibold border transition-colors ${
                  tab === t.id
                    ? 'bg-cyan-400/[0.18] border-cyan-300/40 text-cyan-100'
                    : 'bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06]'
                }`}>
                <t.icon className="h-3.5 w-3.5" />
                {t.label}
              </button>
            ))}
          </div>

          {/* Overview */}
          {tab === 'overview' && overview && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <ReadinessCard label="Security" value={overview.readiness.security} icon={Shield} tint="rgba(34,211,238,0.10)" />
                <ReadinessCard label="SOC 2 Readiness" value={overview.readiness.soc2} icon={CheckCircle} tint="rgba(124,58,237,0.10)" />
                <ReadinessCard label="FERPA" value={overview.readiness.ferpa} icon={Book} tint="rgba(52,211,153,0.10)" />
                <ReadinessCard label="COPPA" value={overview.readiness.coppa} icon={Users} tint="rgba(251,191,36,0.10)" />
                <ReadinessCard label="NC Student Privacy" value={overview.readiness.nc_student_privacy} icon={Lock} tint="rgba(34,211,238,0.10)" />
                <ReadinessCard label="AI Governance" value={overview.readiness.ai_governance} icon={Cpu} tint="rgba(192,132,252,0.10)" />
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <StatCard label="Controls implemented" v={overview.controls_verified} total={overview.controls_total} color="#34d399" />
                <StatCard label="Controls failing" v={overview.controls_failed} total={overview.controls_total} color="#f87171" />
                <StatCard label="High-priority gaps" v={overview.high_failed} total={overview.controls_total} color="#fbbf24" />
              </div>
              <div className="p-5 rounded-2xl border border-white/10 bg-white/[0.03]">
                <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-cyan-300 mb-3">
                  Report generated
                </p>
                <p className="text-sm text-slate-300">{new Date(overview.generated_at).toLocaleString()}</p>
              </div>
            </div>
          )}

          {/* Pilot gate */}
          {tab === 'pilot-gate' && pilotGate && (
            <div className="space-y-6">
              <GateSection title="🔴 Blockers" items={pilotGate.blockers} tint="rgba(248,113,113,0.10)" borderTint="rgba(248,113,113,0.35)" />
              <GateSection title="🟠 High Priority" items={pilotGate.high_priority} tint="rgba(251,191,36,0.10)" borderTint="rgba(251,191,36,0.35)" />
              <GateSection title="🟡 Acceptable with mitigation" items={pilotGate.acceptable_with_mitigation} tint="rgba(96,165,250,0.10)" borderTint="rgba(96,165,250,0.35)" />
              <GateSection title="🟢 Post-pilot" items={pilotGate.post_pilot} tint="rgba(52,211,153,0.10)" borderTint="rgba(52,211,153,0.35)" />
            </div>
          )}

          {/* Controls */}
          {tab === 'controls' && (
            <div className="rounded-2xl border border-white/10 overflow-hidden" style={{ background: 'rgba(13,27,46,0.72)' }}>
              <div className="overflow-x-auto">
                <table className="w-full text-left" data-testid="controls-table">
                  <thead>
                    <tr className="border-b border-white/[0.07] bg-white/[0.02] text-[10.5px] uppercase tracking-[0.14em] text-slate-300">
                      <th className="px-3 py-3">ID</th>
                      <th className="px-3 py-3">Domain</th>
                      <th className="px-3 py-3">Control</th>
                      <th className="px-3 py-3">Priority</th>
                      <th className="px-3 py-3">Status</th>
                      <th className="px-3 py-3">Frameworks</th>
                      <th className="px-3 py-3">Evidence</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04]">
                    {controls.map(c => (
                      <tr key={c.id} data-testid={`control-row-${c.id}`}>
                        <td className="px-3 py-3 text-[11.5px] font-mono text-slate-300">{c.id}</td>
                        <td className="px-3 py-3 text-[12px] text-slate-300">{c.domain}</td>
                        <td className="px-3 py-3 text-[12.5px] text-white">{c.name}</td>
                        <td className="px-3 py-3">
                          <span className={`text-[10.5px] font-semibold ${
                            c.priority === 'CRITICAL' ? 'text-rose-300' :
                            c.priority === 'HIGH' ? 'text-amber-300' : 'text-slate-300'}`}>
                            {c.priority}
                          </span>
                        </td>
                        <td className="px-3 py-3"><StatusPill status={c.status} /></td>
                        <td className="px-3 py-3 max-w-[200px]"><FrameworkTags c={c} /></td>
                        <td className="px-3 py-3 text-[11px] text-slate-300 max-w-[280px]">{c.evidence}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Audit logs */}
          {tab === 'audit' && audit && (
            <div className="grid lg:grid-cols-2 gap-5">
              <LogPanel title="FERPA Access Log" count={audit.ferpa_count_all} events={audit.ferpa_events} />
              <LogPanel title="CDP Audit Trail" count={audit.cdp_count_all} events={audit.cdp_events} />
            </div>
          )}

          {/* Incidents */}
          {tab === 'incidents' && incidents && (
            <div>
              <div className="mb-4 grid grid-cols-4 gap-3">
                {['SEV1', 'SEV2', 'SEV3', 'SEV4'].map(s => (
                  <div key={s} className="p-3 rounded-xl border border-white/10 bg-white/[0.03] text-center">
                    <p className="text-[10.5px] uppercase text-slate-300">{s}</p>
                    <p className="font-outfit text-2xl font-bold text-white">{incidents.by_severity?.[s] || 0}</p>
                  </div>
                ))}
              </div>
              {incidents.incidents.length === 0
                ? <EmptyState icon={CheckCircle} message="No incidents on record" />
                : (
                  <div className="rounded-2xl border border-white/10 overflow-hidden" style={{ background: 'rgba(13,27,46,0.72)' }}>
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-white/[0.07] bg-white/[0.02] text-[10.5px] uppercase tracking-[0.14em] text-slate-300">
                          <th className="px-3 py-3">ID</th>
                          <th className="px-3 py-3">Severity</th>
                          <th className="px-3 py-3">Title</th>
                          <th className="px-3 py-3">Status</th>
                          <th className="px-3 py-3">Detected</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/[0.04]">
                        {incidents.incidents.map(i => (
                          <tr key={i.id}>
                            <td className="px-3 py-3 text-[11.5px] font-mono text-slate-300">{i.id?.slice(0, 8)}</td>
                            <td className="px-3 py-3 text-[12px] text-rose-300 font-semibold">{i.severity}</td>
                            <td className="px-3 py-3 text-[12.5px] text-white">{i.title}</td>
                            <td className="px-3 py-3 text-[11.5px] text-slate-300">{i.status}</td>
                            <td className="px-3 py-3 text-[11px] text-slate-300">{i.created_at ? new Date(i.created_at).toLocaleString() : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
            </div>
          )}

          {/* Vulnerabilities */}
          {tab === 'vulnerabilities' && vulns && (
            <div>
              <div className="mb-4 grid grid-cols-4 gap-3">
                {['critical', 'high', 'medium', 'low'].map(s => (
                  <div key={s} className="p-3 rounded-xl border border-white/10 bg-white/[0.03] text-center">
                    <p className="text-[10.5px] uppercase text-slate-300">{s}</p>
                    <p className="font-outfit text-2xl font-bold text-white">{vulns.by_severity?.[s] || 0}</p>
                  </div>
                ))}
              </div>
              {vulns.vulnerabilities.length === 0
                ? <EmptyState icon={CheckCircle} message="No open vulnerabilities" />
                : null}
            </div>
          )}

          {/* AI Governance */}
          {tab === 'ai-governance' && aiGov && (
            <div className="rounded-2xl border border-white/10 overflow-hidden" style={{ background: 'rgba(13,27,46,0.72)' }}>
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/[0.07] bg-white/[0.02] text-[10.5px] uppercase tracking-[0.14em] text-slate-300">
                    <th className="px-3 py-3">Feature</th>
                    <th className="px-3 py-3">Model</th>
                    <th className="px-3 py-3">Purpose</th>
                    <th className="px-3 py-3">Metered</th>
                    <th className="px-3 py-3">Explainable</th>
                    <th className="px-3 py-3">Human loop</th>
                    <th className="px-3 py-3">Risk</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.04]">
                  {aiGov.ai_features.map(a => (
                    <tr key={a.id}>
                      <td className="px-3 py-3 text-[12.5px] text-white">{a.feature}</td>
                      <td className="px-3 py-3 text-[11.5px] font-mono text-slate-300">{a.model}</td>
                      <td className="px-3 py-3 text-[11.5px] text-slate-300 max-w-[280px]">{a.purpose}</td>
                      <td className="px-3 py-3">{a.metered ? <CheckCircle className="h-4 w-4 text-emerald-300" /> : <XCircle className="h-4 w-4 text-rose-300" />}</td>
                      <td className="px-3 py-3">{a.explainable ? <CheckCircle className="h-4 w-4 text-emerald-300" /> : <XCircle className="h-4 w-4 text-slate-300" />}</td>
                      <td className="px-3 py-3">{a.human_in_loop ? <CheckCircle className="h-4 w-4 text-emerald-300" /> : <XCircle className="h-4 w-4 text-slate-300" />}</td>
                      <td className="px-3 py-3 text-[11.5px] font-semibold" style={{ color: a.risk === 'HIGH' ? '#f87171' : a.risk === 'MEDIUM' ? '#fbbf24' : '#34d399' }}>{a.risk}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Vendors */}
          {tab === 'vendors' && vendors && (
            <div className="rounded-2xl border border-white/10 overflow-hidden" style={{ background: 'rgba(13,27,46,0.72)' }}>
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/[0.07] bg-white/[0.02] text-[10.5px] uppercase tracking-[0.14em] text-slate-300">
                    <th className="px-3 py-3">Vendor</th>
                    <th className="px-3 py-3">Purpose</th>
                    <th className="px-3 py-3">Data received</th>
                    <th className="px-3 py-3">Student PII</th>
                    <th className="px-3 py-3">SOC 2</th>
                    <th className="px-3 py-3">Risk</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.04]">
                  {vendors.subprocessors.map(v => (
                    <tr key={v.id}>
                      <td className="px-3 py-3 text-[12.5px] text-white font-semibold">{v.name}</td>
                      <td className="px-3 py-3 text-[11.5px] text-slate-300">{v.purpose}</td>
                      <td className="px-3 py-3 text-[11.5px] text-slate-300">{v.data_received}</td>
                      <td className="px-3 py-3">{v.student_pii ? <span className="text-rose-300 text-[11px]">Yes</span> : <span className="text-slate-300 text-[11px]">No</span>}</td>
                      <td className="px-3 py-3 text-[11.5px] text-slate-300">{v.soc2}</td>
                      <td className="px-3 py-3 text-[11.5px] font-semibold" style={{ color: v.risk === 'HIGH' ? '#f87171' : v.risk === 'MEDIUM' ? '#fbbf24' : '#34d399' }}>{v.risk}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

function StatCard({ label, v, total, color }) {
  return (
    <div className="p-5 rounded-2xl border border-white/10 bg-white/[0.03]">
      <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-slate-300">{label}</p>
      <div className="mt-2 flex items-end gap-2">
        <span className="font-outfit text-3xl font-bold" style={{ color }}>{v}</span>
        <span className="text-sm text-slate-300 pb-1">/ {total}</span>
      </div>
    </div>
  );
}

function GateSection({ title, items, tint, borderTint }) {
  if (!items || !items.length) return null;
  return (
    <div className="p-5 rounded-2xl border" style={{ background: tint, borderColor: borderTint }}>
      <p className="font-outfit text-lg font-semibold text-white mb-3">{title} <span className="text-slate-300 text-sm">({items.length})</span></p>
      <div className="space-y-2">
        {items.map(i => (
          <div key={i.id} className="flex items-start gap-3 p-3 rounded-lg bg-black/20">
            <ChevronRight className="h-4 w-4 mt-0.5 text-slate-300" />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[11px] font-mono text-slate-300">{i.id}</span>
                <span className="text-[12.5px] font-semibold text-white">{i.name}</span>
                <StatusPill status={i.status} />
              </div>
              <p className="text-[11px] text-slate-300">{i.evidence}</p>
              <p className="text-[10px] text-slate-300 mt-1">Owner: {i.owner}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LogPanel({ title, count, events }) {
  return (
    <div className="rounded-2xl border border-white/10 overflow-hidden" style={{ background: 'rgba(13,27,46,0.72)' }}>
      <div className="px-4 py-3 border-b border-white/[0.07] flex items-center justify-between">
        <p className="font-semibold text-white text-sm">{title}</p>
        <span className="text-[10.5px] text-slate-300">{count.toLocaleString()} total</span>
      </div>
      <div className="max-h-[500px] overflow-y-auto divide-y divide-white/[0.04]">
        {events.length === 0
          ? <p className="p-4 text-sm text-slate-300 text-center">No events yet</p>
          : events.map((e, idx) => (
              <div key={idx} className="p-3 text-[11.5px]">
                <div className="flex items-center gap-2 text-slate-300">
                  <span className="font-mono text-slate-300">{(e.timestamp || e.at || '').slice(0, 19)}</span>
                  <span className="text-cyan-300">{e.actor_role || e.role || 'system'}</span>
                  <span className="text-slate-300">→</span>
                  <span className="text-white">{e.action || e.event || e.type || 'access'}</span>
                </div>
                {(e.student_id || e.resource_id) && (
                  <p className="text-[10.5px] text-slate-300 mt-0.5 font-mono">
                    {(e.student_id || e.resource_id || '').slice(0, 8)}…
                  </p>
                )}
              </div>
            ))}
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, message }) {
  return (
    <div className="p-10 text-center rounded-2xl border border-white/10 bg-white/[0.03]">
      <Icon className="h-8 w-8 mx-auto text-emerald-400" />
      <p className="mt-3 text-sm text-slate-300">{message}</p>
    </div>
  );
}
